/** @format */

import { EmitEvent, EventBusService, Events } from '@shared/data-access/services';
import { signal } from '@angular/core';

import { DateTime } from 'luxon';
import { TimeService } from '@shared/utils/services';

export class Timer {
  public readonly startDate: string;
  public readonly endDate: string;

  public readonly seconds = signal(0);
  public readonly minutes = signal(0);
  public readonly hours = signal(0);
  public readonly days = signal(0);

  private timer: ReturnType<typeof setInterval> | null = null;

  constructor(
    startDate: string,
    endDate: string,
    private readonly eventBusService: EventBusService,
    private readonly timeService: TimeService,
  ) {
    this.startDate = startDate;
    this.endDate = endDate;
  }

  /**
   * Starts the timer
   */
  public startTimer() {
    this.countDownTimer(this.endDate);
  }

  /**
   * Stops the timer and cleans up
   */
  public stopTimer() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  /**
   * Counts down the time based of the end date
   * will fire event when the timer ends
   * Event fired:
   * Events.TimerEnded
   * @param endDate
   */
  private countDownTimer(endDate: string) {
    this.timer = setInterval(() => {
      const now = this.timeService.getCurrentTime();
      // adjust for timezone
      const copy = this.fetchAdjustTime(endDate);
      const distance = this.findDistance(copy, now);

      if (distance <= 0) {
        this.stopTimer();
        this.emitEvent(Events.TimerEnded);
        return;
      }

      this.updateTime(distance);
    }, 1000);
  }

  /**
   * Gets time for current timezone
   * @param date
   */
  public fetchAdjustTime(date: string) {
    // Considering the date is in local time "America/New_York"
    return this.timeService.parseDateFromISO(date, { keepLocalTime: true });
  }

  /**
   * Emits the event to subscribers
   * @param event
   */
  public emitEvent(event: Events) {
    this.eventBusService.emit(new EmitEvent(event));
  }

  /**
   * Finds the distance between 2 dates
   * @param time1
   * @param time2
   */
  public findDistance(time1: DateTime, time2: DateTime) {
    return this.timeService.getDistanceBetweenDates(time1, time2);
  }

  /**
   * Updates the days, hours, min, sec for the distance
   * @param distance
   */
  public updateTime(distance: number) {
    const MILLISECONDS_PER_SECOND = 1000;
    const MILLISECONDS_PER_MINUTE = MILLISECONDS_PER_SECOND * 60;
    const MILLISECONDS_PER_HOUR = MILLISECONDS_PER_MINUTE * 60;
    const MILLISECONDS_PER_DAY = MILLISECONDS_PER_HOUR * 24;

    this.days.set(Math.floor(distance / MILLISECONDS_PER_DAY));
    this.hours.set(Math.floor((distance % MILLISECONDS_PER_DAY) / MILLISECONDS_PER_HOUR));
    this.minutes.set(Math.floor((distance % MILLISECONDS_PER_HOUR) / MILLISECONDS_PER_MINUTE));
    this.seconds.set(Math.floor((distance % MILLISECONDS_PER_MINUTE) / MILLISECONDS_PER_SECOND));
  }
}
